import { useCallback, useMemo } from 'react';
import {
  FieldArrayPath,
  FieldValues,
  UseFieldArrayProps,
  UseFieldArrayReturn,
  get,
  set,
  useFieldArray,
  useFormContext,
} from 'react-hook-form';

/**
 * Versione con tipizzazione migliorata di useFieldArray, con metodi mutanti
 * che riallineano i defaultValues del form dopo ogni mutazione dell'array.
 *
 * Utilizzare al posto di `useFieldArray` da `react-hook-form` in tutti
 * i componenti.
 */
export const useFieldArrayOf = <
  T,
  TFieldValues extends FieldValues = Record<string, T[]>,
  TFieldArrayName extends
    FieldArrayPath<TFieldValues> = FieldArrayPath<TFieldValues>,
  TKeyName extends string = 'id',
>(
  props: UseFieldArrayProps<TFieldValues, TFieldArrayName, TKeyName>,
): UseFieldArrayReturn<TFieldValues, TFieldArrayName, TKeyName> => {
  const result = useFieldArray(props);
  const formContext = useFormContext();
  // Stessa risoluzione di useFieldArray: precedenza al control esplicito,
  // contesto come fallback (null fuori da un FormProvider).
  const control = props.control ?? formContext?.control;

  // Dopo ogni mutazione dell'array, allinea _defaultValues con _formValues
  // per il path dell'array. Senza questo, register() di RHF legge default
  // stale da _defaultValues (che non viene reindiciato da _setFieldArray)
  // e li scrive in _formValues, corrompendo i valori dopo remove/append.
  const syncDefaults = useCallback(() => {
    const current = get((control as any)._formValues, props.name);
    if (Array.isArray(current)) {
      set(
        (control as any)._defaultValues,
        props.name,
        current.map((item: any) => (item != null ? { ...item } : item)),
      );
    }
  }, [control, props.name]);

  const append: typeof result.append = useCallback(
    (...args) => {
      result.append(...args);
      syncDefaults();
    },
    [result.append, syncDefaults],
  );

  const prepend: typeof result.prepend = useCallback(
    (...args) => {
      result.prepend(...args);
      syncDefaults();
    },
    [result.prepend, syncDefaults],
  );

  const insert: typeof result.insert = useCallback(
    (...args) => {
      result.insert(...args);
      syncDefaults();
    },
    [result.insert, syncDefaults],
  );

  const remove: typeof result.remove = useCallback(
    (...args) => {
      result.remove(...args);
      syncDefaults();
    },
    [result.remove, syncDefaults],
  );

  const update: typeof result.update = useCallback(
    (...args) => {
      result.update(...args);
      syncDefaults();
    },
    [result.update, syncDefaults],
  );

  const swap: typeof result.swap = useCallback(
    (...args) => {
      result.swap(...args);
      syncDefaults();
    },
    [result.swap, syncDefaults],
  );

  const move: typeof result.move = useCallback(
    (...args) => {
      result.move(...args);
      syncDefaults();
    },
    [result.move, syncDefaults],
  );

  const replace: typeof result.replace = useCallback(
    (...args) => {
      result.replace(...args);
      syncDefaults();
    },
    [result.replace, syncDefaults],
  );

  return useMemo(
    () => ({
      ...result,
      append,
      prepend,
      insert,
      remove,
      update,
      swap,
      move,
      replace,
    }),
    [result, append, prepend, insert, remove, update, swap, move, replace],
  );
};
